import { CustomRoute } from "@/interfaces";
import VendorManagement from "@/modules/Admin/VendorManagement";
import {
  CheckCircle,
  CircleDollarSign,
  CreditCard,
  FileText,
  LayoutDashboard,
  Repeat,
  User,
  CalendarDays,
  ShoppingCart,
  Percent,
  Receipt,
  Building2,
  AlertTriangle,
  Edit3,
  Gavel,
  ClipboardCheck,
  ListChecks,
  Link2,
} from "lucide-react";
import React from "react";

// Lazy load Finance module components
const Dashboard = React.lazy(() => import("../modules/Finance/Dashboard"));
const CreateInvoice = React.lazy(() => import("../modules/Finance/CreateInvoice"));
const EditProfile = React.lazy(() => import("../modules/Profile/EditProfile"));
const ProjectsApprovals = React.lazy(() => import("../modules/Admin/ProjectsApprovals"));
const OrderApprovalPage = React.lazy(() => import("@/modules/Admin/OMS/OrderApprovals"));
const AllNotifications = React.lazy(() => import("../modules/Notifications/AllNotifications"));
const InvoiceManagement = React.lazy(() => import("../modules/Finance/InvoiceManagement"));
const ApprovalManagement = React.lazy(() => import("../modules/Finance/ApprovalManagement"));
const ClientApprovals = React.lazy(() => import("../modules/Finance/ClientApprovals"));
const VendorApprovals = React.lazy(() => import("../modules/Finance/VendorApprovals"));
const CreateAdminInvoice = React.lazy(() => import("../modules/Admin/Invoices/CreateInvoicePage"));
const InvoiceHistory = React.lazy(() => import("../modules/Admin/Invoices/InvoiceHistory"));
const AllCommissions = React.lazy(() => import("../modules/Finance/CommissionManagement/AllCommissions"));
const CommissionPayouts = React.lazy(() => import("../modules/Admin/CommissionManagement/Payouts"));
const CommissionApprovals = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionApprovals"));
const CommissionConflicts = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionConflicts"));
const CustomCommissions = React.lazy(() => import("../modules/Admin/CommissionManagement/CustomCommissions"));
const CommissionDisputes = React.lazy(() => import("../modules/Admin/CommissionManagement/CommissionDisputes"));
const InvoiceCommissionMapping = React.lazy(() => import("../modules/Admin/CommissionManagement/InvoiceCommissionMapping"));
const CommissionTemplates = React.lazy(() => import("../modules/Finance/CommissionManagement/CommissionTemplates"));
const StripePayment = React.lazy(() => import("../modules/Finance/StripePayment"));
const PaymentHistory = React.lazy(() => import("../modules/Finance/PaymentHistory"));
const VendorBills = React.lazy(() => import("../modules/Finance/ListVendorBills/AllBills"));
const VendorBillApprovals = React.lazy(() => import("../modules/Finance/VendorBillApprovals"));
// const CreateBill = React.lazy(() => import("../modules/Finance/CreateBill/CreateBill"));
const RecurringInvoices = React.lazy(() => import("../modules/Admin/Invoices/RecurringInvoices"));
const NextRecurringInvoices = React.lazy(() => import("../modules/Finance/NextRecurringInvoices"));
const OrdersPage = React.lazy(() => import("@/modules/Admin/OMS/Orders/index.tsx"));
const CreateOrderPage = React.lazy(() => import("@/modules/Admin/OMS/Orders/CreateOrderPage.tsx"));
const ReleaseAndPurchaseOrderReport = React.lazy(() =>
  import("@/modules/Admin/pages/ReleaseAndPurchaseOrderReport").then((m) => ({ default: m.ReleaseAndPurchaseOrderReport }))
);
const DailyReportsPage = React.lazy(() => import("@/modules/Admin/OMS/DailyReports/DailyReportsPage.tsx"));
const ClientManagement = React.lazy(() => import("../modules/Admin/ClientManagement/index"));
const PowerBiPage = React.lazy(() => import("@/modules/SuperAdmin/PowerBi"));
const PowerBiViewer = React.lazy(() => import("@/modules/SuperAdmin/PowerBi/PowerBiViewer"));

/**
 * Finance user routes
 * Finance users see data across all companies without company switching
 * All tables include company column and company filter
 */
const financeRoutes: CustomRoute[] = [
  {
    path: "/",
    id: "Finance",
    element: <Dashboard />,
    menuName: "Dashboard",
    icon: LayoutDashboard,
    highlightOn: ["dashboard"],
  },
  // Shown only to CRM deal approvers (UserMenu.tsx); the server scopes it to their steps.
  {
    path: "/projects-approvals",
    id: "Finance",
    element: <ProjectsApprovals />,
    menuName: "Project Tasks",
    icon: ListChecks,
    highlightOn: ["projects-approvals"],
  },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "QuickBooks",
    icon: CreditCard,
    highlightOn: ["quickbooks"],
    submenu: [
      {
        path: "/quickbooks/invoices",
        id: "Finance",
        element: <InvoiceManagement provider="quickbooks" />,
        menuName: "Invoices",
        icon: FileText,
        highlightOn: ["quickbooks-invoices"],
      },
      {
        path: "/quickbooks/approvals",
        id: "Finance",
        element: <ApprovalManagement provider="quickbooks" />,
        menuName: "Invoice Approvals",
        icon: CheckCircle,
        highlightOn: ["quickbooks-approvals"],
      },
      {
        path: "/quickbooks/recurring-invoices",
        id: "Finance",
        element: <RecurringInvoices />,
        menuName: "Recurring Transactions",
        icon: Repeat,
        highlightOn: ["quickbooks-recurring-invoices"],
      },
      {
        path: "/quickbooks/next-invoices",
        id: "Finance",
        element: <NextRecurringInvoices />,
        menuName: "Next Recurring Invoices",
        icon: CalendarDays,
        highlightOn: ["quickbooks-next-invoices"],
      },
    ],
  },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "NetSuite",
    icon: CircleDollarSign,
    highlightOn: ["netsuite"],
    submenu: [
      {
        path: "/netsuite/invoices",
        id: "Finance",
        element: <InvoiceManagement provider="netsuite" />,
        menuName: "Invoices",
        icon: FileText,
        highlightOn: ["netsuite-invoices"],
      },
      {
        path: "/netsuite/approvals",
        id: "Finance",
        element: <ApprovalManagement provider="netsuite" />,
        menuName: "Invoice Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-approvals"],
      },
      {
        path: "/netsuite/vendor-bills",
        id: "Finance",
        element: <VendorBills />,
        menuName: "Vendor Bills",
        icon: FileText,
        highlightOn: ["netsuite-vendor-bills"],
      },
      {
        path: "/netsuite/vendor-bill-approvals",
        id: "Finance",
        element: <VendorBillApprovals />,
        menuName: "Vendor Bills Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-vendor-bill-approvals"],
      },
      {
        path: "/netsuite/client-approvals",
        id: "Finance",
        element: <ClientApprovals />,
        menuName: "Client Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-client-approvals"],
      },
      {
        path: "/netsuite/vendor-approvals",
        id: "Finance",
        element: <VendorApprovals />,
        menuName: "Vendor Approvals",
        icon: CheckCircle,
        highlightOn: ["netsuite-vendor-approvals"],
      },
      {
        path: "/:provider/approvals/edit/:id",
        id: "Finance",
        element: <CreateAdminInvoice isApprovalEdit={true} />,
        menuName: "Edit Invoice Approval",
        hideInMenu: true,
        highlightOn: ["netsuite-approvals", "quickbooks-approvals"],
      },
      {
        path: "/invoices/create",
        id: "Finance",
        element: <CreateAdminInvoice />,
        menuName: "Clone Invoice",
        hideInMenu: true,
        highlightOn: ["quickbooks-invoices", "netsuite-invoices"],
      },
      {
        path: "/invoices/edit/:id",
        id: "Finance",
        element: <CreateAdminInvoice />,
        menuName: "Edit Invoice",
        hideInMenu: true,
        highlightOn: ["quickbooks-invoices"],
      },
    ],
  },
  {
    path: "/client-management",
    id: "Admin",
    element: <ClientManagement />,
    menuName: "Client Management",
    icon: User,
    highlightOn: ["companies", "company"],
  },
  {
    path: "/vendor-management",
    id: "Admin",
    element: <VendorManagement />,
    menuName: "Vendor Management",
    icon: Building2,
    highlightOn: ["VendorManagement", "organization"],
  },
  // Commission approvers (Super Admin → Commission Approvers) see this as a top-level menu only
  // when the Commissions menu is hidden for them; otherwise it shows as the Commissions submenu
  // entry below (UserMenu.tsx). This entry is the one that registers the route.
  {
    path: "/my-commission-approvals",
    id: "Finance",
    element: <CommissionApprovals />,
    menuName: "Commission Approvals",
    icon: ClipboardCheck,
    highlightOn: ["my-commission-approvals"],
  },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "Commissions",
    icon: Percent,
    highlightOn: ["commissions"],
    submenu: [
      {
        path: "/commissions",
        id: "Finance",
        element: <AllCommissions />,
        menuName: "All Commissions",
        icon: FileText,
        highlightOn: ["all-commissions"],
      },
      {
        path: "/commissions/payouts",
        id: "Finance",
        element: <CommissionPayouts />,
        menuName: "Payouts",
        icon: Receipt,
        highlightOn: ["commission-payouts"],
      },
      {
        // Menu link only -- the route itself is registered by the top-level entry.
        path: "/my-commission-approvals",
        id: "Finance",
        element: null,
        menuName: "Commission Approvals",
        icon: ClipboardCheck,
        highlightOn: ["my-commission-approvals"],
      },
      {
        path: "/commissions/conflicts",
        id: "Finance",
        element: <CommissionConflicts />,
        menuName: "Conflicts",
        icon: AlertTriangle,
        highlightOn: ["commission-conflicts"],
      },
      {
        path: "/commissions/custom",
        id: "Finance",
        element: <CustomCommissions />,
        menuName: "Custom Commissions",
        icon: Edit3,
        highlightOn: ["commission-custom"],
      },
      {
        path: "/commissions/disputes",
        id: "Finance",
        element: <CommissionDisputes />,
        menuName: "Disputes",
        icon: Gavel,
        highlightOn: ["commission-disputes"],
      },
      {
        path: "/commissions/invoice-mapping",
        id: "Finance",
        element: <InvoiceCommissionMapping />,
        menuName: "Invoice Mapping",
        icon: Link2,
        highlightOn: ["commission-invoice-mapping"],
      },
      {
        path: "/commissions/templates",
        id: "Finance",
        element: <CommissionTemplates />,
        menuName: "Templates",
        icon: FileText,
        highlightOn: ["commission-templates"],
      },
    ],
  },
  {
    path: "/payments",
    id: "Finance",
    element: <StripePayment />,
    menuName: "Stripe Payments",
    icon: CreditCard,
    highlightOn: ["payments"],
  },
  {
    path: "/payment-history",
    id: "Finance",
    element: <PaymentHistory />,
    menuName: "Payment History",
    icon: Receipt,
    highlightOn: ["payment-history"],
  },
  {
    path: "/create-invoice",
    id: "Finance",
    element: <CreateInvoice />,
    menuName: "Create Invoice",
    icon: FileText,
    highlightOn: ["create-invoice"],
  },
  {
    path: "/billing/invoice-history",
    id: "Finance",
    element: <InvoiceHistory />,
    menuName: "Invoice History",
    icon: Building2,
    highlightOn: ["invoice-history"],
  },
  // {
  //   path: "/create-bill",
  //   id: "Finance",
  //   element: <CreateBill />,
  //   menuName: "Create Bill",
  //   icon: FileText,
  //   highlightOn: ["create-bill"],
  // },
  {
    path: "",
    id: "Finance",
    element: null,
    menuName: "OMS",
    icon: ShoppingCart,
    highlightOn: ["oms", "orders", "approvals"],
    permissionKey: "oms",
    submenu: [
      {
        path: "/oms/orders",
        id: "Finance",
        element: <OrdersPage />,
        menuName: "Orders",
        highlightOn: ["orders"],
      },
      {
        path: "/oms/orders/create",
        id: "Finance",
        element: <CreateOrderPage />,
        hideInMenu: true,
        menuName: "Orders",
        highlightOn: ["orders"],
      },
      {
        path: "/oms/orders/:id/edit",
        id: "edit-order-route",
        element: <CreateOrderPage />,
        menuName: "Edit Order",
        highlightOn: ["orders", "edit-order"],
        hideInMenu: true,
      },
      // Orders that aren't part of a CRM project (L1 Ops → L4 Margin); a CRM project's order is
      // approved on its Create Order step in Project Tasks
      {
        path: "/oms/approvals",
        id: "Finance",
        element: <OrderApprovalPage />,
        menuName: "Order Approvals",
        highlightOn: ["approvals"],
      },
      {
        path: "/oms/ro-po-report",
        id: "Finance",
        element: <ReleaseAndPurchaseOrderReport />,
        menuName: "RO/PO Report",
        highlightOn: ["ro-po-report"],
      },
      {
        path: "/oms/consumption-report",
        id: "Finance",
        element: <DailyReportsPage />,
        menuName: "Consumption Report",
        highlightOn: ["daily-reports"],
        permissionKey: "oms_operations",
      },
    ],
  },
  {
    path: "/powerbi",
    id: "Finance",
    element: <PowerBiPage />,
    menuName: "Power BI",
    icon: FileText,
    highlightOn: ["powerbi"],
  },
  {
    path: "/powerbi/view/:id",
    id: "Finance",
    element: <PowerBiViewer />,
    menuName: "Power BI Viewer",
    hideInMenu: true,
    highlightOn: ["powerbi"],
  },
  {
    path: "/notifications",
    id: "Finance",
    element: <AllNotifications />,
    menuName: "Notifications",
    hideInMenu: true,
    highlightOn: ["notifications"],
  },
  {
    path: "/profile",
    id: "Finance",
    element: <EditProfile />,
    menuName: "Profile",
    icon: User,
    highlightOn: ["profile"],
  },
];

export default financeRoutes;
